import { usePanelStore } from "../../../state/panel";
import { usePortfolioStore } from "../../../state/portfolio";
import { BinStrip } from "../widgets/BinStrip";
import { CohortGrid } from "../widgets/CohortGrid";
import { DisputeTimeline } from "../widgets/DisputeTimeline";
import { EthocaAlertQueue } from "../widgets/EthocaAlertQueue";
import { HeadlineStats } from "../widgets/HeadlineStats";
import { IntelGraph } from "../widgets/IntelGraph";
import { MerchantProfile } from "../widgets/MerchantProfile";
import { RetentionForecast } from "../widgets/RetentionForecast";
import { SiteAuditCard } from "../widgets/SiteAuditCard";
import { TC40MatchHeatmap } from "../widgets/TC40MatchHeatmap";
import { VampSparkline } from "../widgets/VampSparkline";
import { WorstOffenders } from "../widgets/WorstOffenders";
import styles from "./Intelligence.module.css";

export function IntelligenceView() {
  const focus = usePanelStore((s) => s.focus);
  const reveal = usePanelStore((s) => s.reveal);
  const portfolio = usePortfolioStore((s) => s.portfolio);

  const crumbLabel = labelFor(focus, portfolio);

  return (
    <div className={styles.view}>
      {focus.kind !== "portfolio" && (
        <nav className={styles.crumb} aria-label="panel trail">
          <button
            type="button"
            className={styles.crumbRoot}
            onClick={() => reveal("intelligence", { kind: "portfolio" })}
          >
            ◈ portfolio
          </button>
          <span className={styles.crumbSep}>/</span>
          <span className={styles.crumbTail}>{crumbLabel}</span>
        </nav>
      )}

      {focus.kind === "merchant" && renderMerchantFocus(focus.merchantId, portfolio)}
      {focus.kind === "ethoca" && (
        <EthocaAlertQueue alerts={portfolio.ethoca} merchants={portfolio.merchants} />
      )}
      {focus.kind === "tc40" && <TC40MatchHeatmap records={portfolio.tc40} />}
      {focus.kind === "cohort" && (
        <CohortGrid merchants={portfolio.merchants} filter={focus.filter} />
      )}
      {focus.kind === "retention" && <RetentionForecast portfolio={portfolio} />}
      {focus.kind === "intel-graph" && (
        <IntelGraph intel={portfolio.intel} merchants={portfolio.merchants} />
      )}
      {focus.kind === "portfolio" && (
        <>
          <HeadlineStats portfolio={portfolio} />
          <VampSparkline
            monthly={portfolio.monthly}
            aboveStandard={portfolio.acquirer.vampAboveStandard}
            excessive={portfolio.acquirer.vampExcessive}
          />
          <BinStrip acquirer={portfolio.acquirer} />
          <WorstOffenders portfolio={portfolio} />
          <CohortGrid merchants={portfolio.merchants} />
        </>
      )}
    </div>
  );
}

function renderMerchantFocus(
  merchantId: string,
  portfolio: ReturnType<typeof usePortfolioStore.getState>["portfolio"]
) {
  const merchant = portfolio.merchants.find((m) => m.id === merchantId);
  if (!merchant) return null;
  const audit = portfolio.siteAudits.find((a) => a.merchantId === merchant.id);
  return (
    <>
      <MerchantProfile merchant={merchant} monthly={portfolio.monthly} />
      {audit && <SiteAuditCard merchant={merchant} audit={audit} />}
      <DisputeTimeline
        merchant={merchant}
        tc40={portfolio.tc40}
        ethoca={portfolio.ethoca}
        evidence={portfolio.evidence}
      />
    </>
  );
}

function labelFor(
  focus: ReturnType<typeof usePanelStore.getState>["focus"],
  portfolio: ReturnType<typeof usePortfolioStore.getState>["portfolio"]
): string {
  switch (focus.kind) {
    case "merchant": {
      const m = portfolio.merchants.find((x) => x.id === focus.merchantId);
      return m ? `${m.name} · ${m.id}` : focus.merchantId;
    }
    case "ethoca":
      return "ethoca / cdrn queue";
    case "tc40":
      return "ce 3.0 match map";
    case "cohort":
      return focus.filter ? `cohort · ${focus.filter}` : "cohort grid";
    case "retention":
      return "retention forecast";
    case "intel-graph":
      return "cross-merchant intel";
    case "portfolio":
      return "portfolio";
  }
}
